import React from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Slider,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import type { FilterValue } from "../../../models/Filters/FilterValue";
import { FilterProductsType } from "../../../models/Products/Product";
import CustomTypography from "../common/form/CustomTypography";
import FormTextField from "../common/form/FormTextField";
import AppButton from "../common/buttons/AppButton";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";
import AppDropDown from "../common/filter/AppDropDown";

export interface ListingFiltersPanelProps {
  marginBottom?: string | number;
  expanded: boolean;
  onToggleExpanded: () => void;

  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;

  // Categories
  categoryFilters: FilterValue[];
  selectedCategoryIds: number[];
  onSelectedCategoryIdsChange: (ids: number[]) => void;

  // Active (optional)
  showActive?: boolean;
  activeFilters?: FilterValue[];
  activeValue?: string; // "true" | "false"
  onActiveValueChange?: (value: string) => void;
  showProductsTypeFilter?: boolean;
  productsTypeFilters?: FilterValue[];
  productsTypeValue?: string;
  onProductsTypeValueChange?: (value: string) => void;

  // Price (optional)
  showPrice?: boolean;
  priceRangeLabel?: string;
  minPrice: number;
  maxPrice: number;
  priceRange: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  minPriceInput: string;
  maxPriceInput: string;
  onMinPriceInputChange: (value: string) => void;
  onMaxPriceInputChange: (value: string) => void;

  // Actions
  onApply: () => void;
  onClear: () => void;
}

const ListingFiltersPanel: React.FC<ListingFiltersPanelProps> = ({
  marginBottom = 2,
  expanded,
  onToggleExpanded,

  searchValue,
  onSearchChange,
  onSearchKeyDown,
  onSearchSubmit,

  categoryFilters,
  selectedCategoryIds,
  onSelectedCategoryIdsChange,

  showActive = false,
  activeFilters = [],
  activeValue = "true",
  onActiveValueChange,
  showProductsTypeFilter = false,
  productsTypeFilters = [],
  productsTypeValue = FilterProductsType.AllProducts,
  onProductsTypeValueChange,

  showPrice = true,
  priceRangeLabel = "Price Range",
  minPrice,
  maxPrice,
  priceRange,
  onPriceRangeChange,
  minPriceInput,
  maxPriceInput,
  onMinPriceInputChange,
  onMaxPriceInputChange,

  onApply,
  onClear,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: marginBottom,
        borderRadius: 2,
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <Box
        onClick={onToggleExpanded}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 0.5,
          cursor: "pointer",
          borderBottom: expanded ? "1px solid" : "none",
          borderColor: "divider",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <CustomTypography variant="heading7">Search & Filters</CustomTypography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "nowrap",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <FormTextField
                id="listing-search"
                placeholder="Search by product name or Id"
                type="search"
                variant="outlined"
                sx={{ mb: 0 }}
                value={searchValue}
                onChange={(_, value) => onSearchChange(value || "")}
                onKeyDown={onSearchKeyDown}
              />
            </Box>
            <AppButton
              variant="primary"
              label="Search"
              onClick={onSearchSubmit}
              customStyles={{ minWidth: 140, height: 40 }}
            />
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ width: 260 }}>
              {categoryFilters.length !== 0 && (
                <Box>
                  <AppSearchDropdownMulti
                    title="Select Category"
                    fontSize="0.8rem"
                    isMultiSelect={true}
                    value={selectedCategoryIds.map((id) => ({
                      id: String(id),
                      value:
                        categoryFilters.find((category) => String(category.id) === String(id))
                          ?.value || String(id),
                    }))}
                    list={categoryFilters}
                    local={true}
                    handleSelect={(selectedItems) =>
                      onSelectedCategoryIdsChange(selectedItems.map((item) => Number(item.id)))
                    }
                  />
                </Box>
              )}
            </Box>

            {showActive && activeFilters.length !== 0 && (
              <Box sx={{ width: 200 }}>
                <AppDropDown
                  title=""
                  fontSize="12px"
                  isMultiSelect={false}
                  value={activeValue}
                  list={activeFilters}
                  onSingleSelectionChange={(id) => onActiveValueChange?.(id)}
                />
              </Box>
            )}

            {showProductsTypeFilter && productsTypeFilters.length !== 0 && (
              <Box sx={{ width: 240 }}>
                <AppDropDown
                  title="Product Classification Type"
                  fontSize="12px"
                  isMultiSelect={false}
                  value={productsTypeValue}
                  list={productsTypeFilters}
                  onSingleSelectionChange={(id) =>
                    onProductsTypeValueChange?.(id)
                  }
                />
              </Box>
            )}

            {showPrice && (
              <>
                <Box sx={{ width: 240, px: 1 }}>
                  <Box sx={{ mb: 0.5, display: "inline-block" }}>
                    <CustomTypography variant="body3">
                      {priceRangeLabel}: {priceRange[0]} - {priceRange[1]}
                    </CustomTypography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box sx={{ flex: 1 }}>
                      <Slider
                        value={priceRange}
                        min={minPrice}
                        max={maxPrice}
                        step={100}
                        onChange={(_, newValue) => {
                          if (Array.isArray(newValue) && newValue.length === 2) {
                            onPriceRangeChange([newValue[0], newValue[1]]);
                          }
                        }}
                        valueLabelDisplay="auto"
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Box sx={{ width: 90 }}>
                    <FormTextField
                      id="min-price"
                      type="number"
                      placeholder="Min"
                      value={minPriceInput}
                      onChange={(_, value) => onMinPriceInputChange(value || "")}
                      sx={{ mb: 0 }}
                    />
                  </Box>
                  <Box sx={{ width: 90 }}>
                    <FormTextField
                      id="max-price"
                      type="number"
                      placeholder="Max"
                      value={maxPriceInput}
                      onChange={(_, value) => onMaxPriceInputChange(value || "")}
                      sx={{ mb: 0 }}
                    />
                  </Box>
                </Box>
              </>
            )}

            <Box>
              <AppButton
                variant="primary"
                label="Apply"
                onClick={onApply}
                customStyles={{ minWidth: 120 }}
              />
            </Box>
            <Box>
              <AppButton
                variant="transparentPrimary"
                label="Clear"
                onClick={onClear}
                customStyles={{ minWidth: 120 }}
              />
            </Box>
          </Box>

          {selectedCategoryIds.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                width: "100%",
                display: "flex",
                flexWrap: "nowrap",
                gap: 0.5,
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                pb: 0.5,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: 4,
                },
              }}
            >
              {selectedCategoryIds.map((id) => {
                const category = categoryFilters.find((item) => Number(item.id) === Number(id));
                if (!category) return null;
                return (
                  <Chip
                    key={`category-${id}`}
                    size="small"
                    label={category.value}
                    onDelete={() => onSelectedCategoryIdsChange(selectedCategoryIds.filter((x) => x !== id))}
                    sx={{
                      fontSize: "0.625rem",
                      height: "24px",
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default React.memo(ListingFiltersPanel);

